import {
  CloseOutlined,
  ExpandAltOutlined,
  ExportOutlined,
  HistoryOutlined,
  PlusOutlined,
  ReadOutlined,
} from '@ant-design/icons'
import assistantStore from '@stores/assistantStore'
import { observer } from 'mobx-react'
import { ReactNode, useEffect, useRef } from 'react'

import { useAssistantSession } from './AssistantSessionContext'
import Composer from './Composer'
import ConversationList from './ConversationList'
import EmailCitationModal from './EmailCitationModal'
import AssistantGuide from './Guide/AssistantGuide'
import MessageList from './MessageList'
import PlacementConversationList from './PlacementConversationList'
import { useAssistantPlacement } from './placements/PlacementContext'
import PlacementQuickQuestions from './placements/PlacementQuickQuestions'
import PrivacyNotice from './PrivacyNotice'
import { AssistantAvatar, IconGhostButton, KW } from './ui'

interface Props {
  /** Rendered on the standalone page (/view/assistant) instead of the drawer. */
  standalone?: boolean
  /**
   * Rendered in a pane of a screen (the Case history's "Ask K-Assist") instead of the drawer: the
   * panel is only mounted while the pane is shown, and its Close button hides the pane.
   */
  embedded?: {
    onClose: () => void
    /** The Close button's name, e.g. "Back to history" where the pane replaces the screen. */
    closeTitle?: string
    closeIcon?: ReactNode
    /**
     * "Open in full K-Assist": hands this conversation to the drawer. The button is shown when
     * the placement has it (header.buttons.openInDrawer) and the host gives this.
     */
    onOpenInDrawer?: () => void
  }
}

const SUBTITLE = 'AI-Powered Intelligence for Kawaconn'

const BetaBadge = () => (
  <span
    className="rounded-full px-[7px] py-px text-[10px] font-extrabold tracking-wider"
    style={{
      color: KW.teal500,
      background: '#e7f7fb',
      border: `1px solid ${KW.softBorder}`,
    }}
  >
    BETA
  </span>
)

/**
 * The whole Assistant (header, chat, history, guide) — hosted by the drawer inside the app, by
 * the standalone page in its own browser tab, or by a pane of a screen (embedded). What the
 * header, the empty chat and the composer show is the placement's (useAssistantPlacement): the
 * drawer and the standalone page are 'default-drawer'.
 */
const AssistantPanel = ({ standalone = false, embedded }: Props) => {
  const session = useAssistantSession()
  const placement = useAssistantPlacement()
  const { header } = placement
  const { buttons } = header
  const compact = header.variant === 'compact'
  // A History of the placement's own (only its conversations about its record), or null for
  // the drawer's list of every conversation.
  const placementHistory = session.placementHistory

  // Back from a History of the placement's own: the focus goes back to the History button.
  // After the chat is shown again, so the composer (which takes the focus when it mounts) does
  // not keep it.
  const historyButtonRef = useRef<HTMLButtonElement>(null)
  const returnFocus = session.historyReturnFocus
  const returnFocusRef = useRef(returnFocus)
  useEffect(() => {
    if (returnFocusRef.current === returnFocus) {
      return
    }
    returnFocusRef.current = returnFocus
    historyButtonRef.current?.focus()
  }, [returnFocus])

  // 'context': the placement and the record it is about ("Case history · IME-10234").
  const context = session.placementContext
  const subtitle =
    header.subtitle === 'context'
      ? [placement.displayName, context?.label?.trim()]
          .filter(Boolean)
          .join(' · ')
      : SUBTITLE

  const closeButton = embedded ? (
    <IconGhostButton
      title={embedded.closeTitle ?? 'Close'}
      icon={embedded.closeIcon ?? <CloseOutlined />}
      onClick={embedded.onClose}
    />
  ) : (
    !standalone && (
      <IconGhostButton
        title="Close"
        icon={<CloseOutlined />}
        onClick={assistantStore.close}
      />
    )
  )

  // The conversation goes to the drawer only between two answers (and confirm/cancels), and
  // not while the drawer is answering in its own conversation.
  const onOpenInDrawer = buttons.openInDrawer
    ? embedded?.onOpenInDrawer
    : undefined
  const handOffBlocked =
    !!onOpenInDrawer && !assistantStore.canHandOffToDrawer(session)
  const actions = (
    <>
      {buttons.newChat && (
        <IconGhostButton
          title="New chat"
          icon={<PlusOutlined />}
          disabled={session.isTurnRunning}
          onClick={session.newConversation}
        />
      )}
      {onOpenInDrawer && (
        <IconGhostButton
          title={
            handOffBlocked
              ? 'Wait for K-Assist to finish answering'
              : 'Open in full K-Assist'
          }
          ariaLabel="Open in full K-Assist"
          icon={<ExpandAltOutlined />}
          // Still focusable while blocked, so a keyboard or screen-reader user hears why.
          ariaDisabled={handOffBlocked}
          description={
            handOffBlocked ? 'Wait for K-Assist to finish answering' : undefined
          }
          onClick={onOpenInDrawer}
        />
      )}
      {buttons.guide && (
        <IconGhostButton
          title="Guide"
          icon={<ReadOutlined />}
          active={session.showGuide}
          onClick={session.toggleGuide}
        />
      )}
      {buttons.history && (
        <IconGhostButton
          title="History"
          icon={<HistoryOutlined />}
          active={session.showHistory}
          buttonRef={historyButtonRef}
          onClick={
            placementHistory && session.showHistory
              ? session.leaveHistory
              : session.toggleHistory
          }
        />
      )}
      {buttons.openInNewWindow && !standalone && (
        <IconGhostButton
          title="Open in new window"
          icon={<ExportOutlined />}
          disabled={session.isBusy}
          onClick={session.openInNewWindow}
        />
      )}
    </>
  )

  return (
    // data-assistant-panel: the Composer takes the focus back only from inside the panel.
    <div className="flex h-full min-h-0 flex-col" data-assistant-panel>
      {compact ? (
        // One 44px row: the name and the record it is about, then the placement's buttons.
        <div
          className="flex h-[44px] flex-shrink-0 items-center gap-[8px] px-[10px]"
          style={{ borderBottom: `1px solid ${KW.line}` }}
        >
          {closeButton}
          <div className="flex min-w-0 flex-1 items-center gap-[8px]">
            <AssistantAvatar size={22} />
            <div className="flex min-w-0 flex-col">
              <span
                className="text-[13.5px] font-extrabold leading-[17px]"
                style={{ color: KW.ink }}
              >
                K-Assist
              </span>
              <span
                className="truncate text-[11px] font-semibold leading-[14px]"
                style={{ color: KW.muted }}
                title={subtitle}
              >
                {subtitle}
              </span>
            </div>
          </div>
          {actions}
        </div>
      ) : (
        <div
          className="flex h-[58px] flex-shrink-0 items-center gap-[11px] px-[14px]"
          style={{ borderBottom: `1px solid ${KW.line}` }}
        >
          {closeButton}
          <div className="flex min-w-0 flex-1 items-center gap-[9px]">
            <AssistantAvatar size={26} />
            <div className="flex min-w-0 flex-col">
              <div className="flex items-center gap-[9px]">
                <span
                  className="text-base font-extrabold leading-5"
                  style={{ color: KW.ink }}
                >
                  K-Assist
                </span>
                <BetaBadge />
              </div>
              <span
                className="truncate text-[11px] font-semibold leading-4"
                style={{ color: KW.muted }}
              >
                {subtitle}
              </span>
            </div>
          </div>
          {actions}
        </div>
      )}

      {session.showHistory ? (
        placementHistory ? (
          <PlacementConversationList
            history={placementHistory}
            onBack={session.leaveHistory}
          />
        ) : (
          <ConversationList />
        )
      ) : session.showGuide && buttons.guide ? (
        // Never in a placement without the Guide button: nothing there would lead back.
        <AssistantGuide standalone={standalone} />
      ) : assistantStore.privacyNotice ? (
        // Read once before the first question; the history and the guide stay within reach.
        <PrivacyNotice />
      ) : (
        <>
          <MessageList />
          <PlacementQuickQuestions />
          <Composer visible={embedded ? true : undefined} />
        </>
      )}
      {/* The app renders the email modal once (AssistantWidget); the standalone page has no
        widget, so its panel does. */}
      {standalone && <EmailCitationModal />}
    </div>
  )
}

export default observer(AssistantPanel)
